import CompanyBrandingService from '@apis/CompanyBrandingService';
import { CompanyBrandingData } from '@customTypes/CompanyBranding';
import { DEFAULT_BRANDING } from '@constants/branding';
import BrandingManagerService from '@services/BrandingManagerService';
import { useAppStore } from '@stores/app/appStore';
import { BrandingDOMUtils } from '@utils/BrandingDOMUtils';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

type BrandingCache = {
  data: CompanyBrandingData | null;
  timestamp: number;
};

type BrandingActions = {
  getBranding: (forceRefresh?: boolean) => Promise<void>;
  saveBranding: (branding: FormData) => Promise<void>;
  saveOnBoardingBranding: (branding: FormData) => Promise<void>;
  setBranding: (branding: CompanyBrandingData | null) => void;
  updateLocalBranding: (branding: Partial<CompanyBrandingData>) => void;
  getCompanyName: () => string;
  discardChanges: () => void;
  applyBranding: (branding: CompanyBrandingData) => void;
  generatePreview: (branding: CompanyBrandingData) => void;
  setDefaultBranding: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  clearCache: () => void;
};

interface BrandingState {
  // Server/managed branding data
  managedBranding: CompanyBrandingData | null;
  // Local/editing branding data
  localBranding: CompanyBrandingData | null;
  // Preview data for live preview
  previewBranding: CompanyBrandingData | null;
  loading: boolean;
  error: string | null;
  cache: BrandingCache;
  // Track if local changes exist
  hasUnsavedChanges: boolean;
}

type BrandingStore = BrandingState & BrandingActions;

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const initialState: BrandingState = {
  managedBranding: DEFAULT_BRANDING,
  localBranding: DEFAULT_BRANDING,
  previewBranding: DEFAULT_BRANDING,
  loading: false,
  error: null,
  cache: { data: DEFAULT_BRANDING, timestamp: 0 },
  hasUnsavedChanges: false,
};

const isCacheValid = (timestamp: number): boolean =>
  Date.now() - timestamp < CACHE_TTL;

export const useBrandingStore = create<BrandingStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    getCompanyName: () => {
      const { getCompanyName } = useAppStore.getState();
      return getCompanyName();
    },

    getBranding: async (forceRefresh = false) => {
      const { cache, setBranding, setDefaultBranding, getCompanyName } = get();
      const companyName = getCompanyName();

      console.log(`Loading branding for company: ${companyName}`);

      if (!forceRefresh && cache.data && isCacheValid(cache.timestamp)) {
        const fullBranding = BrandingManagerService.generateBrandingFromColors(
          cache.data,
        );
        set({
          managedBranding: fullBranding,
          localBranding: fullBranding,
          previewBranding: fullBranding,
          hasUnsavedChanges: false,
        });

        return;
      }

      set({ loading: true, error: null });
      try {
        const branding = await CompanyBrandingService.getBranding(companyName);
        setBranding(branding);
      } catch (error) {
        console.error('error', error);
        // set({
        //   error:
        //     error instanceof Error ? error.message : 'Failed to fetch branding',
        //   cache: { data: null, timestamp: 0 },
        // });
        setDefaultBranding();
      } finally {
        set({ loading: false });
      }
    },

    saveBranding: async (branding: Partial<CompanyBrandingData>) => {
      const { getCompanyName } = get();
      const companyName = getCompanyName();

      if (!companyName) {
        set({ error: 'No company specified', loading: false });
        return;
      }

      set({ loading: true, error: null });

      try {
        await CompanyBrandingService.saveBranding(companyName, branding);

        // Refresh from server to get the latest data
        await get().getBranding(true);
      } catch (error: any) {
        set({
          error:
            error instanceof Error ? error.message : 'Failed to save branding',
        });
      } finally {
        set({ loading: false });
      }
    },

    saveOnBoardingBranding: async (branding: FormData) => {
      const { getCompanyName, localBranding } = get();
      const companyName = getCompanyName();

      if (!companyName) {
        set({ error: 'No company specified', loading: false });
        return;
      }

      set({ loading: true, error: null });

      try {
        await CompanyBrandingService.saveOnBoardingBranding(
          companyName,
          branding,
        );

        if (localBranding) {
          set({
            managedBranding: localBranding,
            hasUnsavedChanges: false,
          });
        }
      } catch (error: any) {
        set({
          error:
            error instanceof Error ? error.message : 'Failed to save branding',
        });
        throw error; 
      } finally {
        set({ loading: false });
      }
    },

    setBranding: (branding) => {
      const fullBranding = branding
        ? BrandingManagerService.generateBrandingFromColors(branding)
        : null;

      set({
        managedBranding: fullBranding,
        localBranding: fullBranding,
        previewBranding: fullBranding,
        cache: { data: fullBranding, timestamp: Date.now() },
        hasUnsavedChanges: false,
      });

      if (fullBranding) {
        BrandingDOMUtils.applyBranding(fullBranding);
        BrandingDOMUtils.updateMetaInfo(fullBranding);
      }
    },

    updateLocalBranding: (brandingUpdate) => {
      const { localBranding, managedBranding } = get();
      const updatedBranding = { ...localBranding, ...brandingUpdate };

      set({
        localBranding: updatedBranding as CompanyBrandingData,
        hasUnsavedChanges:
          JSON.stringify(updatedBranding) !== JSON.stringify(managedBranding),
      });
    },

    generatePreview: (branding) => {
      const fullBranding =
        BrandingManagerService.generateBrandingFromColors(branding);
      set({ previewBranding: fullBranding });

      if (fullBranding) {
        BrandingDOMUtils.applyBranding(fullBranding);
      }
    },

    applyBranding: (branding) => {
      BrandingDOMUtils.applyBranding(branding);
    },

    setDefaultBranding: () => {
      const defaultBranding =
        BrandingManagerService.generateBrandingFromColors(DEFAULT_BRANDING);
      set({
        managedBranding: defaultBranding,
        localBranding: defaultBranding,
        previewBranding: defaultBranding,
        hasUnsavedChanges: false,
      });
      BrandingDOMUtils.applyBranding(defaultBranding);
      BrandingDOMUtils.updateMetaInfo(defaultBranding);
    },
    discardChanges: () => {
      const { managedBranding } = get();
      set({
        localBranding: managedBranding,
        previewBranding: managedBranding,
        hasUnsavedChanges: false,
      });

      if (managedBranding) {
        BrandingDOMUtils.applyBranding(managedBranding);
      }
    },
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    clearCache: () => set({ cache: { data: null, timestamp: 0 } }),
    reset: () => set(initialState),
  })),
);

// Selectors for optimized subscriptions
export const useBrandingSelectors = {
  managedBranding: () => useBrandingStore((state) => state.managedBranding),
  localBranding: () => useBrandingStore((state) => state.localBranding),
  previewBranding: () => useBrandingStore((state) => state.previewBranding),
  loading: () => useBrandingStore((state) => state.loading),
  error: () => useBrandingStore((state) => state.error),
  hasUnsavedChanges: () => useBrandingStore((state) => state.hasUnsavedChanges),
};
